import React, { useEffect, useState } from 'react'
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  Chip
} from '@mui/material'
import { hashHistory } from 'react-router'
import Grid from '@material-ui/core/Grid'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import TablePaginationInc from '../../../../../shared-ui/TablePaginationInc'
import {
  formatDateLocale,
  formatPepMatchScore
} from '../../../../../lib/utils.es6'
import { useProductCurrencies } from '../../../../../hooks/useProductCurrency'
import { getPepPolicies } from '../../../../../lib/requests.es6'
import { highRiskRatingFilter } from '../../lib/riskRating'

var DEFAULT_SIZE = 10

var TAB_CONFIGS = [
  { status: 'Un_Confirmed', pep_flagged: 'true' },
  { status: 'Rejected', pep_flagged: 'true' },
  { status: 'Unpaid', pep_flagged: 'false' }
]

const DisplayPepPolicies = () => {
  var [awaitingApprovalPolicies, setAwaitingApprovalPolicies] = useState([])
  var [rejectedPolicies, setRejectedPolicies] = useState([])
  var [
    approvedAwaitingPaymentPolicies,
    setApprovedAwaitingPaymentPolicies
  ] = useState([])

  var [awaitingApprovalTotal, setAwaitingApprovalTotal] = useState(0)
  var [rejectedTotal, setRejectedTotal] = useState(0)
  var [
    approvedAwaitingPaymentTotal,
    setApprovedAwaitingPaymentTotal
  ] = useState(0)

  var [loading, setLoading] = useState(false)
  var [tab, setTab] = useState(0)

  var [awaitingPage, setAwaitingPage] = useState(0)
  var [awaitingSize, setAwaitingSize] = useState(DEFAULT_SIZE)
  var [rejectedPage, setRejectedPage] = useState(0)
  var [rejectedSize, setRejectedSize] = useState(DEFAULT_SIZE)
  var [approvedPage, setApprovedPage] = useState(0)
  var [approvedSize, setApprovedSize] = useState(DEFAULT_SIZE)

  var resolveCurrency = useProductCurrencies(
    awaitingApprovalPolicies
      .concat(rejectedPolicies, approvedAwaitingPaymentPolicies)
      .map(function(policy) {
        return policy && policy.product_code
      })
  )

  var pageSetter = [
    [setAwaitingApprovalPolicies, setAwaitingApprovalTotal],
    [setRejectedPolicies, setRejectedTotal],
    [setApprovedAwaitingPaymentPolicies, setApprovedAwaitingPaymentTotal]
  ]

  var fetchTab = function(tabIndex, page, size) {
    var cfg = TAB_CONFIGS[tabIndex]
    var setList = pageSetter[tabIndex][0]
    var setTotal = pageSetter[tabIndex][1]
    setLoading(true)
    getPepPolicies({
      status: cfg.status,
      pep_flagged: cfg.pep_flagged,
      customer_risk_rating: highRiskRatingFilter(),
      page: page + 1,
      size: size
    }).then(function(response) {
      var data =
        response && response.data ? response.data : { data: [], total: 0 }
      setList(data.data || [])
      setTotal(data.total || 0)
    }).catch(function() {
      setList([])
      setTotal(0)
    }).finally(function() {
      setLoading(false)
    })
  }

  var pageStates = [
    { page: awaitingPage, size: awaitingSize },
    { page: rejectedPage, size: rejectedSize },
    { page: approvedPage, size: approvedSize }
  ]

  useEffect(function() {
    var ps = pageStates[tab]
    fetchTab(tab, ps.page, ps.size)
    // eslint-disable-next-line
  }, [
    tab,
    awaitingPage, awaitingSize,
    rejectedPage, rejectedSize,
    approvedPage, approvedSize
  ])

  var getCurrentTabData = function() {
    if (tab === 1) {
      return {
        policies: rejectedPolicies, total: rejectedTotal,
        page: rejectedPage, size: rejectedSize
      }
    }
    if (tab === 2) {
      return {
        policies: approvedAwaitingPaymentPolicies,
        total: approvedAwaitingPaymentTotal,
        page: approvedPage, size: approvedSize
      }
    }
    return {
      policies: awaitingApprovalPolicies, total: awaitingApprovalTotal,
      page: awaitingPage, size: awaitingSize
    }
  }

  var handleTabChange = function(_, v) {
    setTab(v)
    var resetters = [setAwaitingPage, setRejectedPage, setApprovedPage]
    resetters[v](0)
  }

  var handlePaginationChange = function(newPage, newSize) {
    var setters = [
      [setAwaitingPage, setAwaitingSize],
      [setRejectedPage, setRejectedSize],
      [setApprovedPage, setApprovedSize]
    ]
    setters[tab][0](newPage)
    setters[tab][1](newSize)
  }

  var tabData = getCurrentTabData()

  var getPrincipalName = function(policy) {
    if (
      policy.additional_details &&
      policy.additional_details.principal_full_names
    ) {
      return policy.additional_details.principal_full_names
    }
    if (
      policy.additional_details &&
      policy.additional_details.mainMember &&
      policy.additional_details.mainMember.fullName
    ) {
      return policy.additional_details.mainMember.fullName
    }
    if (
      policy.quotation &&
      policy.quotation.policy_schedule_entries &&
      policy.quotation.policy_schedule_entries.length > 0
    ) {
      var principalEntry = policy.quotation.policy_schedule_entries.find(
        function(entry) {
          return entry.dependant &&
            entry.dependant.relationship === 'Principal'
        }
      )
      if (principalEntry && principalEntry.dependant) {
        var firstName = principalEntry.dependant.first_name || ''
        var lastName = principalEntry.dependant.last_name || ''
        var fullName = (firstName + ' ' + lastName).trim()
        if (fullName) return fullName
      }
    }
    if (
      policy.additional_details &&
      (policy.additional_details.first_name ||
        policy.additional_details.last_name)
    ) {
      var fn = policy.additional_details.first_name || ''
      var ln = policy.additional_details.last_name || ''
      var name = (fn + ' ' + ln).trim()
      if (name) return name
    }
    return 'N/A'
  }

  var renderPolicyCard = function(policy, index) {
    var principalName = getPrincipalName(policy)
    var premium =
      policy.quotation && policy.quotation.total_premium_amount_in_cents
        ? resolveCurrency(policy.product_code) + ' ' +
          policy.quotation.total_premium_amount_in_cents / 100
        : 'N/A'

    return (
      <Card
        key={policy.guid || index}
        sx={{
          mb: 2,
          boxShadow: 3,
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: '#2d3748', mb: 1 }}
              >
                {policy.policy_number || 'N/A'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 1 }}>
                <strong>Principal:</strong> {principalName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                <strong>Beneficiary:</strong> {policy.beneficiary_name || 'N/A'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 1
              }}
            >
              {tab === 0 &&
                policy.additional_details &&
                policy.additional_details.pep_flagged && (
                  <Chip
                    label="PEP FLAGGED"
                    color="error"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}

              {tab === 1 && (
                <Chip
                  label={`PEP ${
                    policy.additional_details &&
                    policy.additional_details.pep_action
                      ? policy.additional_details.pep_action.toUpperCase()
                      : 'REJECTED'
                  }`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}

              {tab === 2 && (
                <Chip
                  label={`PEP ${
                    policy.additional_details &&
                    policy.additional_details.pep_action
                      ? policy.additional_details.pep_action.toUpperCase()
                      : 'APPROVED'
                  }`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}

              <Chip
                label={policy.active ? 'Active' : 'Inactive'}
                color={policy.active ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Status
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {policy.status || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Premium
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {premium}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Created
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDateLocale(policy.created_at) || 'N/A'}
              </Typography>
            </Grid>

            {policy.additional_details &&
              policy.additional_details.pep_position && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    PEP Position
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {policy.additional_details.pep_position}
                  </Typography>
                </Grid>
              )}

            {policy.additional_details &&
              formatPepMatchScore(policy.additional_details.match_score) && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    PEP Match Score
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatPepMatchScore(policy.additional_details.match_score)}
                  </Typography>
                </Grid>
              )}

            {tab === 1 &&
              policy.additional_details &&
              policy.additional_details.pep_reason && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Reason
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                  >
                    {policy.additional_details.pep_reason.length > 50
                      ? policy.additional_details.pep_reason.substring(
                          0,
                          50
                        ) + '...'
                      : policy.additional_details.pep_reason}
                  </Typography>
                </Grid>
              )}

            {tab === 2 &&
              policy.additional_details &&
              policy.additional_details.pep_approved_by && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Approved By
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {policy.additional_details.pep_approved_by}
                  </Typography>
                </Grid>
              )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <ButtonInc
              onClick={() =>
                hashHistory.push({
                  pathname: `admin/policy-processing/${policy.policy_number}`,
                  query: { from: 'pep_dashboard' }
                })
              }
              variant="contained"
              sx={{
                minWidth: 120,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              View Policy
            </ButtonInc>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <React.Fragment>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="Awaiting Review" />
          <Tab label="Rejected" />
          <Tab label="Approved, Awaiting Payment" />
        </Tabs>
      </Box>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: '#718096' }}>
            Loading policies...
          </Typography>
        </Box>
      ) : tabData.policies.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: '#718096' }}>
            No policies found
          </Typography>
        </Box>
      ) : (
        <Box>
          {tabData.policies.map(renderPolicyCard)}
          <TablePaginationInc
            page={tabData.page}
            size={tabData.size}
            total={tabData.total}
            onChange={handlePaginationChange}
          />
        </Box>
      )}
    </React.Fragment>
  )
}

export default DisplayPepPolicies
